import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Container,
  Card,
  CardContent,
  useTheme,
  Fade,
  Slide,
  Chip,
  AppBar,
  IconButton,
  Stack,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from "@mui/material";
import {
  Home as HomeIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Star as StarIcon,
  CleaningServices as CleaningServicesIcon,
  Payment as PaymentIcon,
  SupportAgent as SupportIcon,
  Groups as GroupsIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  PhoneIphone as MobileIcon,
  QrCode2 as QRIcon,
  FormatQuote as QuoteIcon,
  Apple as AppleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccountCircle as AccountIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  LocationOn as LocationIcon,
  EmojiEvents as AwardIcon,
  School as TrainingIcon,
  Shield as ShieldIcon,
  People as TeamIcon,
  TrendingUp as GrowthIcon
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
`;

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const CleanerHome = () => {
  const theme = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [applicationForm, setApplicationForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    experience: "",
    location: "",
    availability: ""
  });

  // Stats for cleaners
  const cleanerStats = [
    { number: "$35-50", label: "Per Hour" },
    { number: "500+", label: "Active Cleaners" },
    { number: "4.8★", label: "Cleaner Rating" },
    { number: "95%", label: "Work Satisfaction" }
  ];

  // Benefits for cleaners
  const benefits = [
    {
      icon: <MoneyIcon sx={{ fontSize: 40 }} />,
      title: "Competitive Pay",
      description: "Earn $35-50 per hour with tips and bonuses"
    },
    {
      icon: <CalendarIcon sx={{ fontSize: 40 }} />,
      title: "Flexible Schedule",
      description: "Choose your own hours and work when you want"
    },
    {
      icon: <TrainingIcon sx={{ fontSize: 40 }} />,
      title: "Free Training",
      description: "Comprehensive training and ongoing support"
    },
    {
      icon: <ShieldIcon sx={{ fontSize: 40 }} />,
      title: "Insurance Coverage",
      description: "Liability insurance for your peace of mind"
    },
    {
      icon: <TeamIcon sx={{ fontSize: 40 }} />,
      title: "Support Team",
      description: "Dedicated support team available 24/7"
    },
    {
      icon: <GrowthIcon sx={{ fontSize: 40 }} />,
      title: "Career Growth",
      description: "Opportunities to grow into team lead positions"
    }
  ];

  // How it works steps
  const steps = [
    {
      label: "Apply Online",
      description: "Fill out our simple application form in 5 minutes",
      icon: "📝"
    },
    {
      label: "Background Check",
      description: "Quick and secure background verification",
      icon: "🔍"
    },
    {
      label: "Training & Onboarding",
      description: "Complete our free online training program",
      icon: "🎓"
    },
    {
      label: "Start Working",
      description: "Accept cleaning jobs and start earning immediately",
      icon: "💼"
    }
  ];

  // Testimonials from existing cleaners
  const cleanerTestimonials = [
    {
      id: 1,
      name: "Maria Rodriguez",
      role: "Professional Cleaner",
      location: "Miami, FL",
      rating: 5,
      comment: "I've been cleaning with FreshSweeper for over a year and it's changed my life. The flexible schedule lets me pick up my kids from school, and I'm earning more than I ever did at my previous job.",
      avatar: "MR",
      earnings: "$45/hr average"
    },
    {
      id: 2,
      name: "James Wilson",
      role: "Cleaning Team Lead",
      location: "Austin, TX",
      rating: 5,
      comment: "The support from FreshSweeper is incredible. They handle all the client communication and scheduling, so I can focus on what I do best - cleaning!",
      avatar: "JW",
      earnings: "$52/hr average"
    },
    {
      id: 3,
      name: "Sarah Chen",
      role: "Professional Cleaner",
      location: "San Diego, CA",
      rating: 5,
      comment: "I love being my own boss while still having the security of consistent work. The app makes it so easy to manage my schedule and track my earnings.",
      avatar: "SC",
      earnings: "$48/hr average"
    }
  ];

  // Requirements to become a cleaner
  const requirements = [
    {
      icon: "🔞",
      title: "18+ Years Old",
      description: "Must be at least 18 years of age"
    },
    {
      icon: "🇺🇸",
      title: "Work Authorization",
      description: "Legal right to work in the United States"
    },
    {
      icon: "📱",
      title: "Smartphone",
      description: "iPhone or Android with app compatibility"
    },
    {
      icon: "🚗",
      title: "Reliable Transportation",
      description: "Ability to travel to cleaning locations"
    },
    {
      icon: "💼",
      title: "Cleaning Experience",
      description: "Previous cleaning experience preferred"
    },
    {
      icon: "⭐",
      title: "Great Attitude",
      description: "Professional and customer-focused approach"
    }
  ];

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFormChange = (field, value) => {
    setApplicationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitApplication = () => {
    // Handle form submission
    console.log("Application submitted:", applicationForm);
    // You would typically send this to your backend
    alert("Application submitted successfully! We'll be in touch soon.");
  };

  return (
    <Box sx={{ overflow: "hidden", background: theme.palette.background.default }}>
      {/* Navigation */}
      

      {/* Hero Section */}
      <Box sx={{ pt: 12, pb: 8, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={800}>
                <Box sx={{ color: theme.palette.primary.contrastText }}>
                  <Chip 
                    label="💼 Join Our Team" 
                    sx={{ 
                      mb: 3,
                      background: alpha(theme.palette.primary.contrastText, 0.2),
                      color: theme.palette.primary.contrastText,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      backdropFilter: "blur(10px)"
                    }}
                  />
                  
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                      fontWeight: 700,
                      lineHeight: 1.1,
                      mb: 2
                    }}
                  >
                    Become a
                    <Box
                      component="span"
                      sx={{
                        color: theme.palette.secondary.main,
                        display: "block"
                      }}
                    >
                      Professional Cleaner
                    </Box>
                  </Typography>
                  
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 4,
                      fontSize: "1.1rem",
                      lineHeight: 1.6,
                      fontWeight: 400,
                      opacity: 0.9
                    }}
                  >
                    Join FreshSweeper and earn great money with flexible hours. 
                    Be your own boss while we handle the clients, scheduling, and payments.
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 6 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: "1rem",
                        background: alpha(theme.palette.primary.contrastText, 0.2),
                        backdropFilter: "blur(10px)",
                        borderRadius: "8px",
                        border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.3)}`,
                        fontWeight: 600,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          background: alpha(theme.palette.primary.contrastText, 0.3),
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.3s ease"
                      }}
                      onClick={() => document.getElementById('application-section').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Start Your Application
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: "1rem",
                        borderColor: theme.palette.primary.contrastText,
                        color: theme.palette.primary.contrastText,
                        borderRadius: "8px",
                        "&:hover": {
                          background: alpha(theme.palette.primary.contrastText, 0.1),
                        }
                      }}
                    >
                      View Earnings
                    </Button>
                  </Stack>

                  {/* Stats */}
                  <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {cleanerStats.map((stat, index) => (
                      <Box key={index}>
                        <Typography variant="h5" fontWeight="700" sx={{ opacity: 0.9 }}>
                          {stat.number}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: "500" }}>
                          {stat.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Slide in timeout={1000} direction="left">
                <Box
                  sx={{
                    position: "relative",
                    animation: `${floatAnimation} 6s ease-in-out infinite`
                  }}
                >
                  <Card
                    sx={{
                      background: alpha(theme.palette.background.paper, 0.1),
                      backdropFilter: "blur(20px)",
                      borderRadius: "16px",
                      p: 4,
                      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
                      border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.2)}`,
                      color: theme.palette.primary.contrastText
                    }}
                  >
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                      Why Clean with FreshSweeper?
                    </Typography>
                    
                    <Stack spacing={2}>
                      {[
                        { icon: "💵", text: "Earn $35-50 per hour" },
                        { icon: "📅", text: "Choose your own schedule" },
                        { icon: "🛡️", text: "Liability insurance included" },
                        { icon: "🎓", text: "Free training provided" },
                        { icon: "⭐", text: "Keep 100% of your tips" }
                      ].map((item, index) => (
                        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "8px",
                              background: alpha(theme.palette.primary.contrastText, 0.2),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "1.1rem"
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography variant="body1" fontWeight="500">
                            {item.text}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 10, background: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: "2.25rem", md: "2.75rem" }
              }}
            >
              Why Choose FreshSweeper?
            </Typography>
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, maxWidth: "600px", mx: "auto" }}>
              We provide everything you need to build a successful cleaning business
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    background: theme.palette.background.paper,
                    borderRadius: "16px",
                    p: 4,
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: "all 0.3s ease",
                    textAlign: "center",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)",
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "20px",
                      background: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.primary.main,
                      margin: "0 auto 24px",
                      animation: `${pulseAnimation} 2s ease-in-out infinite`
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography variant="h5" fontWeight="600" color={theme.palette.text.primary} gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body1" color={theme.palette.text.secondary}>
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: 12, background: theme.palette.background.default }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: "2.25rem", md: "2.75rem" }
              }}
            >
              How to Get Started
            </Typography>
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, maxWidth: "600px", mx: "auto" }}>
              Start earning in as little as one week
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          background: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.primary.contrastText,
                          fontWeight: "bold",
                          fontSize: "1.2rem"
                        }}
                      >
                        {step.icon}
                      </Box>
                    )}
                  >
                    <Typography variant="h6" fontWeight="600" color={theme.palette.text.primary}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body1" color={theme.palette.text.secondary} sx={{ mb: 2 }}>
                      {step.description}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ mr: 1 }}
                      >
                        {index === steps.length - 1 ? 'Finish' : 'Continue'}
                      </Button>
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                      >
                        Back
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Container>
      </Box>

      {/* Requirements Section */}
      <Box sx={{ py: 10, background: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: "2.25rem", md: "2.75rem" }
              }}
            >
              Requirements to Join
            </Typography>
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, maxWidth: "600px", mx: "auto" }}>
              Here's what you need to get started
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {requirements.map((requirement, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    background: theme.palette.background.paper,
                    borderRadius: "12px",
                    p: 3,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "12px",
                      background: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.primary.main,
                      fontWeight: "bold",
                      fontSize: "1.5rem",
                      margin: "0 auto 16px"
                    }}
                  >
                    {requirement.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="600" color={theme.palette.text.primary} gutterBottom>
                    {requirement.title}
                  </Typography>
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    {requirement.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Cleaner Testimonials */}
      <Box sx={{ py: 12, background: theme.palette.background.default }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip 
              label="👥 From Our Team" 
              sx={{ 
                mb: 3,
                background: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.dark,
                fontWeight: 600,
                fontSize: "0.85rem"
              }}
            />
            
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: "2.25rem", md: "2.75rem" }
              }}
            >
              Hear From Our Cleaners
            </Typography>
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, maxWidth: "600px", mx: "auto" }}>
              Real stories from cleaners who built successful careers with FreshSweeper
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {cleanerTestimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={testimonial.id}>
                <Fade in timeout={800 + index * 200}>
                  <Card
                    sx={{
                      height: "100%",
                      background: theme.palette.background.paper,
                      borderRadius: "16px",
                      p: 4,
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                      }
                    }}
                  >
                    <QuoteIcon 
                      sx={{ 
                        fontSize: 40, 
                        color: alpha(theme.palette.primary.main, 0.2),
                        mb: 2 
                      }} 
                    />
                    
                    <Box sx={{ display: "flex", mb: 2 }}>
                      {[...Array(5)].map((_, i) => (
                        <StarIcon 
                          key={i} 
                          sx={{ 
                            color: i < testimonial.rating ? theme.palette.secondary.main : theme.palette.grey[300],
                            fontSize: 20
                          }} 
                        />
                      ))}
                    </Box>
                    
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: theme.palette.text.primary, 
                        mb: 3,
                        fontSize: "1.1rem",
                        lineHeight: 1.6,
                        fontStyle: "italic"
                      }}
                    >
                      "{testimonial.comment}"
                    </Typography>
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "12px",
                          background: alpha(theme.palette.primary.main, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                          fontSize: "1rem"
                        }}
                      >
                        {testimonial.avatar}
                      </Box>
                      
                      <Box>
                        <Typography variant="h6" fontWeight="600" color={theme.palette.text.primary}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                          {testimonial.role}
                        </Typography>
                        <Typography variant="caption" color={theme.palette.text.secondary}>
                          {testimonial.location} • {testimonial.earnings}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Application Form Section */}
      <Box id="application-section" sx={{ py: 12, background: theme.palette.background.paper }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: "2.25rem", md: "2.75rem" }
              }}
            >
              Start Your Application
            </Typography>
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, maxWidth: "600px", mx: "auto" }}>
              Join our team of professional cleaners. It only takes 5 minutes to apply.
            </Typography>
          </Box>

          <Card
            sx={{
              background: theme.palette.background.paper,
              borderRadius: "16px",
              p: 4,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={applicationForm.fullName}
                  onChange={(e) => handleFormChange('fullName', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={applicationForm.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={applicationForm.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Cleaning Experience</InputLabel>
                  <Select
                    value={applicationForm.experience}
                    label="Cleaning Experience"
                    onChange={(e) => handleFormChange('experience', e.target.value)}
                  >
                    <MenuItem value="none">No experience</MenuItem>
                    <MenuItem value="1-2">1-2 years</MenuItem>
                    <MenuItem value="3-5">3-5 years</MenuItem>
                    <MenuItem value="5+">5+ years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City & State"
                  value={applicationForm.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Availability</InputLabel>
                  <Select
                    value={applicationForm.availability}
                    label="Availability"
                    onChange={(e) => handleFormChange('availability', e.target.value)}
                  >
                    <MenuItem value="full-time">Full-time</MenuItem>
                    <MenuItem value="part-time">Part-time</MenuItem>
                    <MenuItem value="weekends">Weekends only</MenuItem>
                    <MenuItem value="flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    py: 2,
                    fontSize: "1.1rem",
                    background: theme.palette.primary.main,
                    borderRadius: "8px",
                    fontWeight: 600,
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: "all 0.3s ease"
                  }}
                  onClick={handleSubmitApplication}
                >
                  Submit Application
                </Button>
                <Typography variant="body2" color={theme.palette.text.secondary} sx={{ textAlign: 'center', mt: 2 }}>
                  We'll contact you within 24 hours to discuss next steps
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: 12, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", color: theme.palette.primary.contrastText }}>
            <CleaningServicesIcon sx={{ fontSize: 60, mb: 3, opacity: 0.9 }} />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: "2.25rem", md: "2.75rem" }
              }}
            >
              Ready to Start Your Cleaning Career?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: "1.1rem",
                maxWidth: "600px",
                mx: "auto"
              }}
            >
              Join thousands of cleaners who have built successful businesses with FreshSweeper. 
              Flexible hours, great pay, and full support.
            </Typography>
            
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  background: alpha(theme.palette.primary.contrastText, 0.2),
                  backdropFilter: "blur(10px)",
                  borderRadius: "8px",
                  border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.3)}`,
                  fontWeight: 600,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    background: alpha(theme.palette.primary.contrastText, 0.3),
                    transform: "translateY(-1px)"
                  },
                  transition: "all 0.3s ease"
                }}
                onClick={() => document.getElementById('application-section').scrollIntoView({ behavior: 'smooth' })}
              >
                Apply Now
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  borderColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.contrastText,
                  borderRadius: "8px",
                  "&:hover": {
                    background: alpha(theme.palette.primary.contrastText, 0.1),
                  }
                }}
              >
                Contact Support
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CleanerHome;